/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import authentication from './authentication';
import authenticationToken from './authentication-token';
import blueprint from './blueprint';
import blueprintQuestions from '../slice/blueprintQuestions';
import initiativeBlueprintQuestions from './initiative-blueprint-questions';
import currentUser from './current-user';
import customMetricGroups from './custom-metric-groups';
import globalData from './global-data';
import industrySectors from './industry-sectors';
import initiative from './initiative';
import onboardingToken from './onboarding-token';
import peerComparison from './peerComparison';
import publicScorecard from '../slice/publicScorecard';
import reportingFrameworks from './reporting-frameworks';
import scorecard from './scorecard';
import scorecardSurvey from './scorecard-survey';
import siteAlerts from '../slice/siteAlertsSlice';
import survey from './survey';
import surveyConfig from '../slice/surveyConfig';
import surveyAvailable from '../slice/surveyAvailable';
import surveyDelegationUsers from '../slice/surveyDelegationUsers';
import surveyListSummary from './survey-list-summary';
import surveySettings from '../slice/surveySettings';
import tour from './tour';
import universalTrackers from './universal-trackers';
import universalTrackerGroups from './universal-tracker-groups';
import universalTrackerModal from './universal-tracker-modal';
import universalTrackerValueDetails from './universal-tracker-value-details';
import universalTrackerValues from './universal-tracker-values';
import utrvChartData from '../slice/utrvChartDataSlice';
import UNSDGMap from './unsdg-map';
import upgradeModal from './upgrade-modal';
import userTrackers from './user-trackers';
import valueListSlice from '../slice/valueListSlice';
import initiativeSurveyListSlice from '../slice/initiativeSurveyListSlice';
import portfolioTreeSlice from '../slice/portfolioTreeSlice';
import portfolioSlice from '../slice/portfolioSlice';
import glossarySlice from '../slice/glossarySlice';
import rootInitiativeListSlice from '../slice/rootInitiativeListSlice';
import referenceUtrSlice from '../slice/referenceUtrSlice';
import { AnyAction, configureStore, ThunkAction } from '@reduxjs/toolkit';
// oxlint-disable-next-line no-restricted-imports
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { referralsApi } from '@api/referrals';
import { utrvApi } from '@api/utrv';
import { initiativeApi } from '@api/initiative-stats';
import { g17ecoApi } from '@api/g17ecoApi';
import portfolioCompanySlice from '../slice/portfolioCompanySlice';

import { bookmarksApi } from '@api/bookmarks';
import { surveyTemplatesApi } from '@api/survey-templates';
import { surveyTemplatesHistoryApi } from '@api/survey-templates-history';
import { benchmarkingApi } from '@api/benchmarking';
import { defaultSurveyConfigApi } from '@api/default-survey-config';
import { bankingSettingsApi } from '@api/banking-settings';
import { adminDashboardApi } from '@api/admin-dashboard';
import { metricGroupsApi } from '@api/metric-groups';
import { carbonCalculatorsApi } from '@api/carbon-calculators';
import { materialityAssessmentApi } from '../apps/materiality-tracker/api/materiality-assessment';
import config from '../config';

export const reducer = {
  [g17ecoApi.reducerPath]: g17ecoApi.reducer,
  [referralsApi.reducerPath]: referralsApi.reducer,
  [utrvApi.reducerPath]: utrvApi.reducer,
  [initiativeApi.reducerPath]: initiativeApi.reducer,
  [bookmarksApi.reducerPath]: bookmarksApi.reducer,
  [surveyTemplatesApi.reducerPath]: surveyTemplatesApi.reducer,
  [surveyTemplatesHistoryApi.reducerPath]: surveyTemplatesHistoryApi.reducer,
  [benchmarkingApi.reducerPath]: benchmarkingApi.reducer,
  [defaultSurveyConfigApi.reducerPath]: defaultSurveyConfigApi.reducer,
  [bankingSettingsApi.reducerPath]: bankingSettingsApi.reducer,
  [adminDashboardApi.reducerPath]: adminDashboardApi.reducer,
  [metricGroupsApi.reducerPath]: metricGroupsApi.reducer,
  [carbonCalculatorsApi.reducerPath]: carbonCalculatorsApi.reducer,
  [materialityAssessmentApi.reducerPath]: materialityAssessmentApi.reducer,
  authentication,
  authenticationToken,
  blueprint,
  blueprintQuestions,
  currentUser,
  customMetricGroups,
  globalData,
  industrySectors,
  initiative,
  initiativeBlueprintQuestions,
  initiativeSurveyList: initiativeSurveyListSlice,
  onboardingToken,
  peerComparison,
  publicScorecard,
  reportingFrameworks,
  referenceUtr: referenceUtrSlice,
  scorecard,
  scorecardSurvey,
  siteAlerts,
  survey,
  surveyConfig,
  surveyAvailable,
  surveyDelegationUsers,
  surveyListSummary,
  tour,
  universalTrackers,
  universalTrackerGroups,
  universalTrackerModal,
  universalTrackerValues,
  universalTrackerValueDetails,
  utrvChartData,
  UNSDGMap,
  upgradeModal,
  userTrackers,
  valueList: valueListSlice,
  surveySettings,
  portfolioTree: portfolioTreeSlice,
  portfolio: portfolioSlice,
  portfolioCompany: portfolioCompanySlice,
  rootInitiatives: rootInitiativeListSlice,
  glossary: glossarySlice,
};

const devTools = !config.isProd && config.appEnv === 'development';

export const ReduxStore = configureStore({
  reducer,
  devTools,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: Boolean(import.meta.env.REACT_APP_REDUX_SERIALIZABLE_CHECK),
      immutableCheck: devTools && import.meta.env.REACT_APP_REDUX_IMMUTABLE_CHECK ? { warnAfter: 100 } : false,
    })
      .concat(referralsApi.middleware)
      .concat(g17ecoApi.middleware)
      .concat(utrvApi.middleware)
      .concat(initiativeApi.middleware)
      .concat(bookmarksApi.middleware)
      .concat(surveyTemplatesApi.middleware)
      .concat(surveyTemplatesHistoryApi.middleware)
      .concat(benchmarkingApi.middleware)
      .concat(defaultSurveyConfigApi.middleware)
      .concat(bankingSettingsApi.middleware)
      .concat(adminDashboardApi.middleware)
      .concat(metricGroupsApi.middleware)
      .concat(carbonCalculatorsApi.middleware)
      .concat(materialityAssessmentApi.middleware)
});

export type RootState = ReturnType<typeof ReduxStore.getState>;
export type AppDispatch = typeof ReduxStore.dispatch;
export type DefaultThunk = ThunkAction<void, RootState, any, AnyAction>;
export type AsyncThunk<T = unknown> = ThunkAction<Promise<T>, RootState, any, AnyAction>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
